import { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  useAddUserMutation,
  useLazyGetUserValidationQuery,
} from "../../../redux/api/user/userAPI";
import { setIsNewUser } from "../../../redux/features/auth/auth-slice";
import { selectCountry } from "../../../redux/features/user/user-slice";
import useDebounce from "../../../shared/hooks/useDebounce";
import RegisteringUsersDTO from "../../../shared/oversight-core/dtos/request-dtos/registering-users-dto";
import CheckUserAvailabilityResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/check-user-availability-response-dto";
import { AppRoute } from "../../../shared/oversight-core/interfaces/app-routes";
import AppButton from "../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import CheckBox from "../../../shared/oversight-core/ui-elements/check-box/check-box";
import AppInput from "../../../shared/oversight-core/ui-elements/input/app-input";
import {
  emailRegex,
  noSpecialCharsNotAllowOnlyNumbers,
  passwordRegex,
  validateInputTypeNumberRegex,
} from "../../../shared/oversight-core/utils/regex";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/oversight-core/utils/toast";

interface IFormInput extends RegisteringUsersDTO {
  termsAndConditions: boolean;
  confirmPassword: string;
}

const defaultFormValues: IFormInput = {
  firstName: "",
  lastName: "",
  username: "",
  email: "",
  mobileNumber: "",
  password: "",
  confirmPassword: "",
  country: "",
  termsAndConditions: false,
};

const RegistrationForm = () => {
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAndConditions, setTermsAndConditions] = useState<boolean>(false);
  const [errorStyle, setErrorStyle] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [mobileNumberError, setMobileNumberError] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<IFormInput>({
    defaultValues: { ...defaultFormValues },
  });

  const country = useSelector(selectCountry);

  const password = useRef({});
  password.current = watch("password", "");
  const email = watch("email");
  const mobileNumber = watch("mobileNumber");

  const [AddUser, { isLoading: isLoadingAddUser }] = useAddUserMutation();
  const [triggerGetUserValidation] = useLazyGetUserValidationQuery();

  const debouncedMobileNumber = useDebounce(mobileNumber, 1000);
  const debouncedEmail = useDebounce(email, 1000);

  useEffect(() => {
    if (
      debouncedMobileNumber &&
      (debouncedMobileNumber.length === 10 ||
        debouncedMobileNumber.length === 12)
    ) {
      triggerGetUserValidation({
        mobileNumber: debouncedMobileNumber,
      })
        .unwrap()
        .then((response: CheckUserAvailabilityResponseDTO) => {
          setMobileNumberError(!response.userAvailability?.mobile_number?.CEB);
        })
        .catch(() => {
          setMobileNumberError(true);
        });
    }
  }, [triggerGetUserValidation, debouncedMobileNumber]);

  useEffect(() => {
    if (debouncedEmail && emailRegex.test(debouncedEmail)) {
      triggerGetUserValidation({
        email: debouncedEmail,
      })
        .unwrap()
        .then((response: CheckUserAvailabilityResponseDTO) => {
          setEmailError(!response.userAvailability.email.CEB);
        })
        .catch(() => {
          setEmailError(true);
        });
    }
  }, [triggerGetUserValidation, debouncedEmail]);

  useEffect(() => {
    if (!emailError && !mobileNumberError) {
      triggerGetUserValidation({
        email: debouncedEmail,
        mobileNumber: debouncedMobileNumber,
      })
        .unwrap()
        .then((response: CheckUserAvailabilityResponseDTO) => {
          setEmailError(response.userAvailability.email.LECO);
          setMobileNumberError(response.userAvailability.mobile_number.LECO);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [triggerGetUserValidation, emailError, mobileNumberError]);

  const onSubmit = (data: IFormInput) => {
    const { confirmPassword, ...newData } = data;
    if (
      !mobileNumberError &&
      !emailError &&
      confirmPassword === data.password
    ) {
      AddUser({
        ...newData,
        username: newData.email,
        country,
      })
        .unwrap()
        .then(() => {
          showSuccessMessage("User Registration Successfully");
          reset({ ...defaultFormValues });
          dispatch(
            setIsNewUser({
              isNewUser: true,
              email: newData.email,
            })
          );
        })
        .catch(() => {
          showErrorMessage("User Registration Unsuccessful");
        });
    }
  };

  useEffect(() => {
    if (Object.keys(errors).length === 0) {
      setErrorStyle(false);
    } else {
      setErrorStyle(true);
    }
  }, [errors]);

  return (
    <Row className="mt-4">
      <Col>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col>
              <AppInput
                name="firstName"
                placeholder="First Name"
                label="First Name"
                register={register("firstName", {
                  required: "First name is required",
                  minLength: {
                    value: 2,
                    message: "This field must contain at least 2 characters",
                  },
                  maxLength: {
                    value: 50,
                    message:
                      "You have exceeded the maximum number of 50 characters in this field",
                  },
                  pattern: {
                    value: noSpecialCharsNotAllowOnlyNumbers,
                    message:
                      "Entered value can't start/end or contain only white spaces and can't contain only numbers and special characters",
                  },
                })}
                errors={errors}
              />
            </Col>
            <Col>
              <AppInput
                name="lastName"
                placeholder="Last Name"
                label="Last Name"
                register={register("lastName", {
                  required: "Last name is required",
                  minLength: {
                    value: 2,
                    message: "This field must contain at least 2 characters",
                  },
                  maxLength: {
                    value: 50,
                    message:
                      "You have exceeded the maximum number of 50 characters in this field",
                  },
                  pattern: {
                    value: noSpecialCharsNotAllowOnlyNumbers,
                    message:
                      "Entered value can't start/end or contain only white spaces and can't contain only numbers and special characters",
                  },
                })}
                errors={errors}
              />
            </Col>
          </Row>
          <AppInput
            className="mt-3"
            name="mobileNumber"
            placeholder="Mobile Number"
            label="Mobile Number"
            type="number"
            icon={
              (debouncedMobileNumber.length === 10 ||
                debouncedMobileNumber.length === 12) &&
              !mobileNumberError
                ? "check"
                : undefined
            }
            register={register("mobileNumber", {
              required: "Mobile number is required",
              pattern: {
                value: validateInputTypeNumberRegex,
                message: "Please enter a valid Mobile number",
              },
            })}
            errors={errors}
            isSuccess={
              (debouncedMobileNumber.length === 10 ||
                debouncedMobileNumber.length === 12) &&
              !mobileNumberError
            }
            isError={
              (debouncedMobileNumber.length === 10 ||
                debouncedMobileNumber.length === 12) &&
              mobileNumberError
            }
          />
          {(debouncedMobileNumber.length === 10 ||
            debouncedMobileNumber.length === 12) &&
            mobileNumberError && (
              <p className="error">
                Mobile number already exists. Please use a different number
              </p>
            )}
          <AppInput
            className="mt-3"
            name="email"
            placeholder="Enter your email"
            label="Email"
            icon={
              emailRegex.test(debouncedEmail) && !emailError
                ? "check"
                : undefined
            }
            register={register("email", {
              required: "Email is required",
              pattern: {
                value: emailRegex,
                message: "Please enter a valid email",
              },
            })}
            errors={errors}
            isSuccess={emailRegex.test(debouncedEmail) && !emailError}
            isError={emailRegex.test(debouncedEmail) && emailError}
          />
          {emailRegex.test(debouncedEmail) && emailError && (
            <p className="error">
              Email already exists. Please use a different email
            </p>
          )}
          <Row>
            <Col md={6} lg={12} xl={6}>
              <AppInput
                className="mt-3"
                name="password"
                icon={!showPassword ? "visibility_off" : "visibility"}
                placeholder="Password"
                onIconClick={() => setShowPassword(!showPassword)}
                label="Password"
                type={showPassword ? "text" : "password"}
                register={register("password", {
                  required: "Password is required",

                  pattern: {
                    value: passwordRegex,
                    message:
                      "Password must have 6-20 characters and include at least one lowercase letter, one uppercase letter, one numeric and one special character",
                  },
                })}
                errors={errors}
                newPassword={true}
              />
            </Col>
            <Col md={6} lg={12} xl={6}>
              <AppInput
                className="mt-3"
                name="confirmPassword"
                icon={!showConfirmPassword ? "visibility_off" : "visibility"}
                placeholder="Confirm Password"
                onIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                register={register("confirmPassword", {
                  required: "Password confirmation is required",
                  validate: (value) =>
                    value === password.current || "Password doesn't match",
                })}
                errors={errors}
              />
            </Col>
          </Row>
          <Row className="mt-4">
            <Col>
              <span className="d-flex align-item-center justify-content-center text-color-2">
                <CheckBox
                  label=""
                  checked={termsAndConditions}
                  onChange={(checked) => setTermsAndConditions(checked)}
                />
                Accept
                <Link to={"#"} className="mx-1">
                  Terms
                </Link>
                and
                <Link to={"#"} className="mx-1">
                  Conditions
                </Link>
              </span>
            </Col>
          </Row>
          <Row>
            <Col>
              <AppButton
                text="Sign Up"
                className={`mt-4 ${errorStyle && `manual-border-shadow`}`}
                variant="blue"
                isLoading={isLoadingAddUser}
                disabled={!termsAndConditions}
              />
            </Col>
          </Row>
          <Row>
            <Col className="mt-4 text-center text-color-2">
              Already have an account? <Link to={AppRoute.LOGIN}>Login</Link>
            </Col>
          </Row>
        </form>
      </Col>
    </Row>
  );
};

export default RegistrationForm;
